import React, { useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AuthContext from '../../../hooks/authContext';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import CommonContext from '../../../hooks/commonContext';
import { getOneAppointment, updateAppointments } from '../../../services/apiservices/appoinments';
import { CommonConfirmationDialog, CommonSuccessDialog, DragReschduleConfirm } from '../../../components/DialogComponent/SupplyDataDialog';
import { calculateDurationInMinutes, filterAppointments, formatSelectedDateTime } from '../../../services/helpers';
import { Switch } from '@material-ui/core';
import { useMediaQuery } from '@mui/material';


const ITEM_TYPE = "APPOINTMENT";

const DraggableAppointment = ({
    index,
    filteredAppointments,
    appointment,
    client,
    boxHeight,
    setIsModalOpen,
    setSelectedAppointment,
    settingsData,
    topPosition
}) => {
    const [, drag] = useDrag({
        type: ITEM_TYPE,
        item: { appointment },
    });

    return (
        <div
            ref={drag}
            className="appointmentView"
            style={{
                height: `${boxHeight}px`,
                width: `calc(100% / ${filteredAppointments.length})`,
                left: `${(index % filteredAppointments.length) * (100 / filteredAppointments.length)}%`,
                top: `${topPosition}%`,
            }}
            onClick={() => {
                setIsModalOpen(true);
                setSelectedAppointment(appointment);
            }}
        >
            <div className="appointmentView-title">{client?.clientName}</div>
            <div className="appointmentView-details2 mv-10">
                {appointment.apoointmentDetails.forms[0]?.values[1]?.value} - {appointment.apoointmentDetails.forms[0]?.values[2]?.value}
            </div>
            <div className="appointmentView-details mv-10">{appointment.apoointmentDetails.shiftName}</div>
            <div className="appointmentView-details2 mv-10">
                {settingsData?.timeFormat === '24hrs' ? moment(appointment.apoointmentDetails.time, 'h:mm A').format('HH:mm') : appointment.apoointmentDetails.time} - {settingsData?.timeFormat === '24hrs' ? moment(appointment.apoointmentDetails.endTime, 'h:mm A').format('HH:mm') : appointment.apoointmentDetails.endTime}
            </div>
        </div>
    );
};

const DroppableTimeSlot = ({ hour, day, employee, onDrop, children, laptop }) => {
    const slotRef = useRef(null);
    const [, drop] = useDrop({
        accept: ITEM_TYPE,
        drop: (item, monitor) => {
            const { appointment } = item;
            const clientOffset = monitor.getClientOffset();
            
            const slotRect = slotRef.current.getBoundingClientRect();
            const offsetY = clientOffset.y - slotRect.top;
            const slotHeight = laptop ? 80 : 100;
            const quarterHeight = slotHeight / 4;
            
            const differenceFromOld = monitor.getDifferenceFromInitialOffset();
            const timeOld = moment(appointment.apoointmentDetails.time, "hh:mm a");
            const minutesOld = timeOld.hours() * 60 + timeOld.minutes();

            const calPoint = 60 * differenceFromOld.y / slotHeight;
            const finalMins = minutesOld + calPoint;
            
            const nearest15Mins = Math.round(finalMins / 15) * 15;
            const timeNew = moment().startOf('day').add(nearest15Mins, 'minutes');
            
            let adjustedHour = hour;
            let adjustedMinutes = 0;

            
            adjustedHour = timeNew.format("HH");
            adjustedMinutes = timeNew.format("mm");

            onDrop(appointment, adjustedHour, day, employee, adjustedMinutes);
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });

    useEffect(() => {
        drop(slotRef.current);
    }, [drop]);

    return (
        <div ref={slotRef} className="time-slot">
            <div className='border-none border-b border-dotted border-[#ddd] dark:border-none' style={{height:"50%"}}></div>
            {children}
        </div>
    )
};

const DayView = ({ allDays, clientDetails, empNames, employeeDetails, fetchAppointments, clientName, settingsData }) => {
    // const timeSlots = Array.from({ length: 13 }, (_, i) => i + 7);
    const timeZones = [
        { label: "EDT - Eastern", value: "America/New_York" },
        { label: "CDT - Central", value: "America/Chicago" },
        { label: "MDT - Mountain", value: "America/Denver" },
        { label: "PDT - Pacific", value: "America/Los_Angeles" }
    ];
    const auth = useContext(AuthContext);
    const appointmentData = Array.isArray(auth?.appointmentsData) ? auth.appointmentsData : Object.values(auth?.appointmentsData || []);
    const setIsModalOpen = auth.setIsModalOpen;
    const setSelectedAppointment = auth.setSelectedAppointment;
    const [widths, setWidths] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [alertOpen, setAlertOpen] = useState(false);
    const [timeZoneDialog, setTimeZoneDialog] = useState(false);
    const [showSlots, setShowSlots] = useState(false);
    const [showEarlyAppt, setShowEarlyAppt] = useState(false);
    const [showLateAppt, setShowLateAppt] = useState(false);
    const [outsideWorkAlert, setOutsideWorkAlert] = useState(false);
    const [selectData, setSelectData] = useState();
    const [appointmentsDataState, setAppointmentsDataState] = useState();
    const commonContext = useContext(CommonContext);
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const laptop = useMediaQuery('(max-width:1801px)');
    const desktop = useMediaQuery('(min-width: 2350px)');
    const containerRef = useRef(null);
    const minuteToPercentageMap = {
        0: 0,
        15: 25,
        30: 50,
        45: 75
    };

    const formatTime = (hour) => {
        if (settingsData?.timeFormat === '24hrs') {
            return `${hour}`;
        } else {
            const ampm = hour >= 12 ? 'pm' : 'am';
            const formattedHour = hour % 12 || 12;
            return `${formattedHour}${ampm}`;
        }
    };

    function convertTo24Hour(timeString) {
        const [time, modifier] = timeString?.split(" ");
        let [hours] = time?.split(":");

        const normalizedModifier = modifier.toUpperCase();

        hours = parseInt(hours);

        if (normalizedModifier === "PM" && hours !== 12) {
            hours += 12;
        }
        if (normalizedModifier === "AM" && hours === 12) {
            hours = 0;
        }

        return hours;
    }

    const standardStartHour = settingsData?.startTime ? convertTo24Hour(settingsData?.startTime) : 9;
    const standardEndHour = settingsData?.endTime ? convertTo24Hour(settingsData?.endTime) : 17;

    const generateTimeSlots = () => {
        const slots = [];

        if (!showSlots) {
            for (let i = 8; i <= 22; i++) {
                slots.push(i);
            }
        }

        if (showSlots) {
            for (let i = 0; i <= 23; i++) {
                slots.push(i);
            }
        }

        return slots;
    };

    const employeeList = employeeDetails && employeeDetails?.filter((item) => ((Array.isArray(item?.role) && item?.role.includes("Employee")) || item?.role === "Employee"))

    const gruopData = employeeList ? Object.entries(
        employeeList.reduce((map, employee) => {
            if (!map[employee.timeZone]) {
                map[employee.timeZone] = [];
            }
            map[employee.timeZone].push(employee);
            return map;
        }, {})
    )
        : [];

    useEffect(() => {
        setLoading(true);
        let hasEarlyAppointments = false;
        let hasLateAppointments = false;

        const standardStartTime = moment({ hour: standardStartHour, minute: 0 });
        const standardEndTime = moment({ hour: standardEndHour, minute: 0 });

        const expandedWidth = desktop && employeeList?.length <= 5 && auth?.isExpanded ? 450 : 350;
        const staticWidths = laptop ? 250 : expandedWidth;
        const totalEmployees = gruopData?.reduce((count, [_, employees]) => count + employees.length, 0) || 1;
        const containerWidth = containerRef.current?.offsetWidth || 1000;
        const totalAvailableWidth = containerWidth - 100; 

        const dynamicWidth = totalAvailableWidth / totalEmployees;
        const totalWidthofBoxes = employeeList?.length * 300;
        const isWidth = totalAvailableWidth <= totalWidthofBoxes;
        const width = gruopData?.flatMap(([timeZone, employees]) =>
            employees?.map((employee) => {
                const employeeAppointments = appointmentData?.filter((item) => {
                    const appointmentTime = moment(item.apoointmentDetails.time, "hh:mm A");
                    const appointmentDate = moment(item.apoointmentDetails.date).format("MMMM DD YYYY");
                    const currentDate = moment(allDays).format("MMMM DD YYYY");

                    if (appointmentDate === currentDate && appointmentTime.isBefore(standardStartTime)) {
                        hasEarlyAppointments = true;
                    }

                    if (appointmentDate === currentDate && appointmentTime.isAfter(standardEndTime)) {
                        hasLateAppointments = true;
                    }

                    return (
                        item.userId === employee.userId &&
                        appointmentDate === currentDate
                    );
                });
                return employeeAppointments.length ? isWidth ? employeeAppointments.length * 300 : dynamicWidth : isWidth ? staticWidths : dynamicWidth;
            })
        );

        setWidths(width);
        setShowEarlyAppt(hasEarlyAppointments);
        setShowLateAppt(hasLateAppointments);
        setShowSlots(hasEarlyAppointments || hasLateAppointments);
        setLoading(false);
    }, [allDays, employeeDetails, auth?.isExpanded, appointmentData]);

    useEffect(() => {
        if (widths?.length > 0) {
            setLoading(false);
        }
    }, [widths]);

    const handleDrop = async (appointment, newHour, day, employee, adjustedMinutes) => {
        const tz = appointment?.apoointmentDetails?.calendarTimezone || "America/New_York";
    
        const getNowInTimezone = (timeZone) => {
            return new Date(new Date().toLocaleString("en-US", { timeZone }));
        };
        const nowInAppointmentTZ = getNowInTimezone(tz);
    
        const inactiveEmp = employeeDetails?.find((emp) => emp?.userId === appointment?.userId);
    
        const startDateTime = new Date(`${appointment?.apoointmentDetails?.date} ${appointment?.apoointmentDetails?.time}`);
        let endDateTime = new Date(`${appointment?.apoointmentDetails?.date} ${appointment?.apoointmentDetails?.endTime}`);
        
        if (endDateTime <= startDateTime) {
            endDateTime.setDate(endDateTime.getDate() + 1);
        }
    
        const checkInTime = appointment?.checkInDateTime?.seconds ? moment.unix(appointment.checkInDateTime.seconds).tz(tz).format("hh:mm a") : null;
        const checkOutTime = appointment?.checkOutDateTime?.seconds ? moment.unix(appointment.checkOutDateTime.seconds).tz(tz).format("hh:mm a") : null;
    
        const isProteams = appointment.source === "proteams";
        const isActiveEmp = inactiveEmp?.status !== false;
        const hasCheckIn = checkInTime !== null;
        const hasCheckOut = checkOutTime !== null;
        const isBeforeEnd = nowInAppointmentTZ < endDateTime;
        const isClientRole = Array.isArray(auth?.userProfile?.role) ? auth.userProfile.role.includes("Client") : auth?.userProfile?.role === "Client";
    
        const isRescheduleAllowed = isProteams && isActiveEmp && (!hasCheckIn || (hasCheckIn && !hasCheckOut && isBeforeEnd)) && !isClientRole;
    
        if (!isRescheduleAllowed) {
           setAlertOpen(true);
           return;
        } else {
            const startHour = moment({ hour: newHour, minute: adjustedMinutes });
            const duration = appointment.apoointmentDetails.duration;
            const endHour = startHour.clone().add(duration, "minutes");

            const workStartTime = moment({ hour: standardStartHour, minute: 0 });
            const workEndTime = moment({ hour: standardEndHour, minute: 0 });

            const isOutsideWorkHours = startHour.isBefore(workStartTime) || endHour.isAfter(workEndTime);

            // if (settingsData?.standardWorkTime === 'no' && isOutsideWorkHours) {
            //     setOutsideWorkAlert(true);
            // } else {
                const data = await getOneAppointment(appointment?.id, 'appoinments')
                setSelectData(data.appointment);

               const prevHistory = data?.historyDate || [];
               const updatedHistory = prevHistory.includes(data?.appointmentDateTime) ? prevHistory : [...prevHistory, data?.appointmentDateTime];

                const formattedStartTime = startHour.format('hh:mm A');
                const formattedEndTime = endHour.format('hh:mm A');
                const date = moment(appointment?.apoointmentDetails?.date).format('MM/DD/YYYY');
                const appointmentDateTime = formatSelectedDateTime(date, formattedStartTime, employee.timeZone);

                const updatedAppointment = {
                    ...data.appointment,
                    time: formattedStartTime,
                    endTime: formattedEndTime,
                    type: "One Time",
                    userId: employee.userId,
                    calendar: `${employee.firstName} ${employee.lastName}`,
                    calendarTimezone: employee.timeZone,
                    appointmentDateTime: appointmentDateTime,
                    historyDate: updatedHistory,
                };

                setAppointmentsDataState(updatedAppointment);
                setDialogOpen(true);
            // }

        }
    };

    const handleConfirmCloseDialog = () => {
        setDialogOpen(false);
        setAppointmentsDataState();
        setSelectData(null);
    }

    const handelTimezoneConfirmcancel = () => {
        setTimeZoneDialog(false);
        setAppointmentsDataState();
        setSelectData(null);
    }

    const handelAlertClose = () => {
        setAlertOpen(false);
        setAppointmentsDataState();
        setSelectData();
    }

    const handelOutsideWorkAlertClose = () => {
        setOutsideWorkAlert(false);
        setAppointmentsDataState();
        setSelectData();
    }

    const handleConfirmUpdateDialog = async () => {
        const oldTimezone = timeZones.find((item) => item.value === selectData.calendarTimezone)?.label;
        const newTimezone = timeZones.find((item) => item.value === appointmentsDataState.calendarTimezone)?.label;
        const timeZoneConflict = oldTimezone !== newTimezone;

        setDialogOpen(false);
        if (timeZoneConflict) {
            setTimeZoneDialog(true);
        } else {
            commonContext?.setLoader(true);
            try {
                const ids = appointmentsDataState?.id;
                const response = await updateAppointments(appointmentsDataState, null, ids);
                if (response.success) {
                    setAppointmentsDataState();
                    setSelectData(null);
                    await delay(3000);
                    await fetchAppointments();
                } else {
                    console.error("Failed to update appointment:", response.error);
                    setAppointmentsDataState();
                    setSelectData(null);
                }
            } catch (error) {
                console.error("Error updating appointment:", error);
            } finally {
                commonContext?.setLoader(false);
            }
        }
    }

    const handelClickTimezoneConfirm = async () => {
        setTimeZoneDialog(false);
        commonContext?.setLoader(true);
        try {
            const ids = appointmentsDataState?.id;
            const response = await updateAppointments(appointmentsDataState, null, ids);
            if (response.success) {
                setAppointmentsDataState();
                setSelectData(null);
                await delay(3000);
                await fetchAppointments();
            } else {
                console.error("Failed to update appointment:", response.error);
                setAppointmentsDataState();
                setSelectData(null);
            }
        } catch (error) {
            console.error("Error updating appointment:", error);
        } finally {
            commonContext?.setLoader(false);
        }
    }

    const timeSlots = generateTimeSlots();
    const TimeColumn = () => {
        return (
            timeSlots.map((hour, index) => (
                <div key={`time-slot-${index}`} className={`time-column ${hour < standardStartHour ? 'early-slot' : ''} ${hour > standardEndHour ? 'late-slot' : ''}`}>
                    {formatTime(hour)}
                </div>
            ))
        );
    };

    return (
        <>
            {/* {loading ? (
                null
            ) : ( */}
                <>
                    <DndProvider backend={HTML5Backend}>
                        <div className='mainCalendarContair'>
                            <div className="mainContainer dark:bg-[#1A1F2C]" ref={containerRef}>
                                <div className='newTimeColumn2 dark:bg-[#1A1F2C]' style={{ top: auth.isExpanded ? "-1px" : 0 }}>
                                <div className="grid-row-day dark:bg-[#1A1F2C] dark:text-white" style={{display: "grid", width: widths?.length > 0 ? "fit-content" : "", gridTemplateColumns: widths?.length > 0
                                            ? `80px ${widths.map((width) => `${width}px`).join(" ")}` : `80px auto`}}>
                                            <div className="time-column newTimeColumn3 dark:bg-[#1A1F2C] dark:text-white" style={{ height: "25px", borderTop: "2px solid #ddd", padding: "0" }}>
                                                    <Switch
                                                        checked={showSlots}
                                                        onChange={(e) => setShowSlots(e.target.checked)}
                                                        color='primary'
                                                        size='small'
                                                    />
                                            </div>
                                                <div className="day-header dark:bg-[#1A1F2C] dark:text-white" style={{ width: widths?.length > 0 ? (widths?.reduce((sum, num) => sum + num, 0)) : "100%"}}>
                                                    {moment(allDays).format("dddd MMM D")}
                                                </div>
                                        </div>
                                        <div className="grid-row-Time dark:bg-[#1A1F2C] dark:text-white"  style={{display: "grid", gridTemplateColumns: `80px ${widths?.map((width) => `${width}px`).join(" ")}`,}}>
                                            <div className="time-column newTimeColumn3 dark:bg-[#1A1F2C] dark:text-white"><AccessTimeIcon fontSize="small" /></div>
                                            {employeeDetails && gruopData?.map(([timeZone, employees], index) => (
                                                employees?.map((employee) => (
                                                    <div key={employee.userId} className="daily-header  dark:bg-[#1A1F2C] dark:text-white">
                                                        {employee.firstName} {employee.lastName} - {timeZones.find((tz) => tz.value === employee.timeZone)?.label.split(" ")[0]}
                                                    </div>
                                                ))
                                            ))}
                                        </div>
                                    </div>
                                {showEarlyAppt && (<div className="early-border" />)}
                                <div className="calendar-container-day d-flex dark:bg-[#1A1F2C] dark:text-white">
                                    <div className='newTimeColumn dark:bg-[#1A1F2C] dark:text-white'>
                                        <TimeColumn />
                                    </div>
                                    <div className={`${auth.isExpanded ? "dayExtraWidth" : ""}`}>
                                        {timeSlots?.map((hour) => (
                                            <div
                                                key={hour}
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: `${widths?.map((width) => `${width}px`).join(" ")}`,
                                                    // backgroundColor:
                                                    // hour >= standardStartHour && hour < standardEndHour
                                                    // ? "white"
                                                    // : "#eee",
                                                }}
                                                className={`${
                                                        hour >= standardStartHour && hour < standardEndHour
                                                        ? 'bg-white dark:bg-[#1A1F2C]'
                                                        : 'bg-gray-100 dark:bg-gray-600'
                                                    }`}>
                                                {gruopData?.map(([timeZone, employees]) => (
                                                    employees?.map((employee) => {
                                                        const appointmentsForEmployee = appointmentData?.filter(
                                                            (appointment) =>
                                                                appointment.userId === employee.userId &&
                                                                moment(appointment.apoointmentDetails.date).format("MMMM DD YYYY") === moment(allDays).format("MMMM DD YYYY")
                                                        );

                                                        const filteredAppointments = filterAppointments(appointmentsForEmployee, clientName, empNames);

                                                        return (
                                                            <DroppableTimeSlot
                                                                key={`${hour}-${employee.userId}`}
                                                                hour={hour}
                                                                day={allDays}
                                                                employee={employee}
                                                                onDrop={handleDrop}
                                                                laptop={laptop}
                                                            >
                                                                {filteredAppointments?.map((appointment, index) => {
                                                                    const appointmentTimeSlot = settingsData?.timeFormat === '24hrs' ? moment(appointment.apoointmentDetails.time, 'h:mm A').hour() : moment(appointment.apoointmentDetails.time, "hh:mm A").hour();
                                                                    const clientId = appointment.apoointmentDetails.clientId ? appointment.apoointmentDetails.clientId : appointment.clientId;
                                                                    const client = clientDetails?.find((client) => client.id === clientId);
                                                                    const appointmentStartTime = appointment.apoointmentDetails.time;
                                                                    let appointmentEndTime = appointment.apoointmentDetails.endTime;
                                                                    const startTimeMoment = moment(appointmentStartTime, "h:mm a");
                                                                    const endTimeMoment = moment(appointmentEndTime, "h:mm a");

                                                                    const crossesMidnight = endTimeMoment.isBefore(startTimeMoment);
                                                                    
                                                                    if (crossesMidnight) {
                                                                        appointmentEndTime = showSlots ? "11:59 pm" : "10:59 pm";
                                                                    }
                                                                    const duration = calculateDurationInMinutes(appointmentStartTime, appointmentEndTime)
                                                                    const time = moment(appointmentStartTime, "hh:mm a");
                                                                    const minuteStart = time.minute();
                                                                    const topPosition = minuteToPercentageMap[minuteStart] ?? 0;
                                                                    const boxHeight = (duration / 60) * (laptop ? 80 : 100);

                                                                    return appointmentTimeSlot === hour ? (
                                                                        <DraggableAppointment
                                                                            key={`appointment-${appointment.id}`}
                                                                            index={index}
                                                                            filteredAppointments={filteredAppointments}
                                                                            appointment={appointment}
                                                                            client={client}
                                                                            boxHeight={boxHeight}
                                                                            setIsModalOpen={setIsModalOpen}
                                                                            setSelectedAppointment={setSelectedAppointment}
                                                                            settingsData={settingsData}
                                                                            topPosition={topPosition}
                                                                        />
                                                                    ) : null;
                                                                })}
                                                            </DroppableTimeSlot>
                                                        );
                                                    })
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {showLateAppt && (<div className="late-border" />)}
                            </div>
                        </div>
                    </DndProvider>
                </>
            {/* )} */}
            {dialogOpen && (
                <DragReschduleConfirm
                    open={dialogOpen}
                    onSingleAppt={handleConfirmUpdateDialog}
                    auth={selectData}
                    onClose={handleConfirmCloseDialog}
                    client={clientDetails}
                    data={appointmentsDataState}
                    timeZones={timeZones}
                    settingsData={settingsData}
                />
            )}
            {timeZoneDialog && (
                <CommonConfirmationDialog
                    open={timeZoneDialog}
                    onConfirm={handelClickTimezoneConfirm}
                    onClose={handelTimezoneConfirmcancel}
                    title="TimeZone Conflict"
                    message={"Alert, you are rescheduling this appointment in a different Time Zone. Do you wish continue?"}
                    buttonTitle={"Yes"}
                />
            )}
            {alertOpen && (
                <CommonSuccessDialog
                    open={alertOpen}
                    onClose={handelAlertClose}
                    title="Appointment Alert"
                    message="You cannot reschedule past appointments or those that are not from our system."
                />
            )}
            {outsideWorkAlert && (
                <CommonSuccessDialog
                    open={outsideWorkAlert}
                    onClose={handelOutsideWorkAlertClose}
                    title="Appointment Work Time Alert"
                    message="Appointments cannot be rescheduled outside of standard work hours unless the setting is configured to allow it"
                />
            )}
        </>
    );


};

export default DayView;